import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { CloudUpload as UploadIcon, GetApp as DownloadIcon } from '@mui/icons-material';
import AuthService from '../services/AuthService';

interface DatevUploadProps {
  onSuccess?: (data: any) => void;
}

const DatevUpload: React.FC<DatevUploadProps> = ({ onSuccess }) => {
  // Log component rendering to help with debugging
  console.log('DatevUpload component rendering');
  
  // Log the API URL for debugging
  console.log('API URL in DatevUpload:', process.env.REACT_APP_API_URL || '/api');
  const [loading, setLoading] = useState<boolean>(false);
  const [processingStatus, setProcessingStatus] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadedData, setUploadedData] = useState<any>(null);
  
  // Define a type for the valid processing modes
  type ProcessingMode = "process-datev";
  
  // Default endpoint configured in the backend
  const [mode, setMode] = useState<ProcessingMode>("process-datev");
  
  // Mapping of mode to the correct API endpoint
  // Note: REACT_APP_API_URL already includes /api, so we don't need to repeat it here
  const endpointMap: Record<ProcessingMode, string> = {
    "process-datev": "/process-datev"
  };
  
  // Reference to file input for resetting
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const fileSize = file.size / (1024 * 1024); // Convert to MB
    console.log(`Datei ausgewählt: ${file.name}, Größe: ${fileSize.toFixed(2)} MB`);
    
    // Check file size client-side
    if (fileSize > 10) {
      setError(`Dateigröße ist zu groß (${fileSize.toFixed(2)} MB). Maximal erlaubt sind 10 MB.`);
      resetFileInput();
      return;
    }
    
    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    setError(null);
    setSuccess(null);
    setProcessingStatus(`Datei wird hochgeladen (${fileSize.toFixed(2)} MB)...`);

    try {
      // Get the correct endpoint from our mapping
      const endpoint = endpointMap[mode];
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const fullUrl = `${apiUrl}${endpoint}`;
      
      console.log(`Uploading to endpoint: ${fullUrl}`);
      console.log(`API URL environment variable: ${apiUrl}`);
      console.log(`Mode: ${mode}, Endpoint path: ${endpoint}`);
      
      // Create AbortController for timeout - increase for large files
      const controller = new AbortController();
      // 2 minutes timeout for large files
      const timeoutId = setTimeout(() => controller.abort(), 120000); 
      
      const response = await fetch(fullUrl, {
        method: "POST",
        body: formData,
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        mode: 'cors',
        signal: controller.signal,
      }).catch(networkError => {
        clearTimeout(timeoutId);
        console.error('Network error during file upload:', networkError);
        throw new Error("Verbindung zum Server nicht möglich. Bitte prüfen Sie, ob der Backend-Server läuft.");
      });

      // Clear the file input to allow the same file to be uploaded again
      resetFileInput();

      if (!response.ok) {
        if (response.status === 500) {
          throw new Error("Datenbankverbindungsfehler. Bitte kontaktieren Sie Ihren Administrator.");
        } else if (response.status === 401) {
          AuthService.logout();
          throw new Error("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
        } else if (response.status === 413) {
          throw new Error("Dateigröße zu groß. Maximale Dateigröße ist 10 MB.");
        } else {
          // Try to get more detailed error information from the response
          try {
            const errorData = await response.json();
            if (errorData && errorData.error) {
              throw new Error(`Fehler: ${errorData.error}`);
            } else {
              throw new Error(`Fehler beim Verarbeiten der CSV-Datei (Status: ${response.status})`);
            }
          } catch (jsonError) {
            // If we can't parse the JSON, fall back to the status code
            throw new Error(`Fehler beim Verarbeiten der CSV-Datei (Status: ${response.status})`);
          }
        }
      }

      clearTimeout(timeoutId); // Clear timeout after successful response
      
      setProcessingStatus("Verarbeite Antwortdaten...");
      const responseData = await response.json();
      
      // Store the response data
      setUploadedData(responseData);
      
      // Display success message
      const meta = responseData.meta;
      setSuccess(`DATEV-Datei erfolgreich verarbeitet. ${meta.modifiedRows} von ${meta.totalRows} Zeilen aktualisiert.`);
      
      // Pass the data up to the parent if needed
      if (onSuccess) {
        onSuccess(responseData);
      }
      
      setProcessingStatus("");
    } catch (err) {
      console.error('Error in handleFileUpload:', err);
      setError((err as Error).message);
      resetFileInput();
      setProcessingStatus("");
    } finally {
      setLoading(false);
    }
  };
  
  // Function to reset the file input
  const resetFileInput = () => {
    // Reset the file input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Function to download the processed CSV
  const downloadProcessedCSV = () => {
    if (!uploadedData || !uploadedData.updatedCsv) {
      setError("Keine Daten zum Herunterladen");
      return;
    }
    
    const blob = new Blob([uploadedData.updatedCsv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    
    const filename = uploadedData.originalFilename 
      ? `processed_${uploadedData.originalFilename}` 
      : "processed_datev.csv";

    // Create download link
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        DATEV CSV-Datei verarbeiten
      </Typography>
      
      <Box mb={3}>
        <Typography variant="subtitle1" gutterBottom>
          DATEV-Datei verarbeiten
        </Typography>
        
        <Typography variant="body2" gutterBottom>
          Verarbeitet eine DATEV-Datei und prüft für jede DATEV-Buchung gegen die Rechnungsdatenbank, ob es Versandposten zu dieser Rechnung gibt. 
          Wenn ja, wird 'xxx' an KOST2 angefügt. Zusätzlich werden auch Einträge mit Versand-Schlüsselwörtern (DHL, Post, Versand, etc.) im Buchungstext markiert.
        </Typography>
      </Box>
      
      {processingStatus && (
        <Box mb={2} display="flex" alignItems="center" gap={2}>
          <CircularProgress size={20} />
          <Typography>{processingStatus}</Typography>
        </Box>
      )}

      <Box mb={2} display="flex" flexDirection="column" gap={2}>
        <Box display="flex" gap={2} alignItems="center">
          <input 
            ref={fileInputRef}
            accept=".csv" 
            style={{ display: "none" }} 
            id="datev-file-upload" 
            type="file" 
            onChange={handleFileUpload} 
            disabled={loading} 
          />
          <label htmlFor="datev-file-upload">
            <Button 
              variant="contained" 
              component="span" 
              disabled={loading}
              startIcon={<UploadIcon />}
            >
              CSV hochladen
            </Button>
          </label>
          
          {uploadedData && (
            <Button 
              variant="outlined" 
              color="success" 
              onClick={downloadProcessedCSV}
              startIcon={<DownloadIcon />}
            >
              Verarbeitete CSV herunterladen
            </Button>
          )}
        </Box>
        
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          Die CSV-Datei sollte im DATEV-Format vorliegen und Spalten für KOST1 und KOST2 enthalten.
        </Typography>
      </Box>
      
      {uploadedData && uploadedData.meta && (
        <Box mt={3}>
          <Typography variant="subtitle1" gutterBottom>Verarbeitungsergebnis:</Typography>
          
          <Typography variant="body2">
            • {uploadedData.meta.modifiedRows} von {uploadedData.meta.totalRows} Zeilen aktualisiert
          </Typography>
          
          {uploadedData.meta.databaseMatches !== undefined && (
            <Typography variant="body2">
              • {uploadedData.meta.databaseMatches} Einträge durch Datenbankabgleich identifiziert
            </Typography>
          )}
          
          {uploadedData.meta.keywordMatches !== undefined && (
            <Typography variant="body2">
              • {uploadedData.meta.keywordMatches} Einträge durch Schlüsselwortsuche identifiziert
            </Typography>
          )}
          
          {uploadedData.meta.totalInvoicesWithShipping !== undefined && (
            <Typography variant="body2">
              • {uploadedData.meta.totalInvoicesWithShipping} Rechnungen mit Versandpositionen in der Datenbank gefunden
            </Typography>
          )}
        </Box>
      )}

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default DatevUpload;